// src/components/ErrorFallback.tsx
// Full-page fallback rendered by Sentry.ErrorBoundary when an unhandled React error occurs.
// The error is automatically captured by Sentry before this component is shown.
// resetError resets the error boundary state and re-renders the component tree.
// In production the raw error.message is hidden to avoid leaking internal details to users.
// - Added role="alert" aria-live="assertive" aria-atomic="true" to root container for screen reader announcements.
// - Changed dashboard navigation to window.location.replace to avoid a back-button loop to the crashing page.

import { AlertCircle } from 'lucide-react';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

export default function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  return (
    <div role="alert" aria-live="assertive" aria-atomic="true" className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-soft-lg border border-neutral-100 p-8 text-center">
        <div className="flex justify-center mb-5">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
        </div>

        <h1 className="text-xl font-semibold text-neutral-900 mb-2">
          Une erreur inattendue est survenue
        </h1>

        <p className="text-sm text-neutral-500 mb-1">
          L'erreur a été signalée automatiquement. Notre équipe en a été notifiée.
        </p>

        {error?.message && !import.meta.env.PROD && (
          <p className="text-xs text-neutral-400 font-mono bg-neutral-50 rounded-lg px-3 py-2 mt-3 mb-5 text-left break-words">
            {error.message.slice(0, 200)}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button
            onClick={resetError}
            className="flex-1 px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            Réessayer
          </button>
          <button
            onClick={() => { window.location.replace('/dashboard'); }}
            className="flex-1 px-4 py-2.5 bg-white text-neutral-700 text-sm font-medium rounded-lg border border-neutral-200 hover:bg-neutral-50 transition-colors"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    </div>
  );
}
